/**
 * Mocks da vitrine pública (`/api/public/**`).
 * Dataset partilhado com admin via `mock-api-property-store` (memória na sessão).
 * Contratos: docs/contracts/api/public/catalog/*.md
 */
import { of } from 'rxjs';

import { HttpRequest, HttpResponse } from '@angular/common/http';

import type { AdminMockRequestHandler } from '@app/mock-api/handlers/mock-api-admin.handlers';
import {
  type PublicCatalogListItem,
  toPublicCatalogDetail,
  toPublicCatalogListItem,
} from '@app/mock-api/mock-api-property-public.mapper';
import {
  ensureMockApiPropertyStoreSeeded,
  mockApiCities,
  mockApiDistrictsByCity,
  mockApiProperties,
} from '@app/mock-api/mock-api-property-store';
import type { MockCity, MockDistrict } from '@app/mock-api/mock-api-shared-models';

function ok<T>(data: T) {
  return { errors: [] as { code: string; messageKey: string }[], data, success: true };
}

function fail(status: number, code: string, messageKey: string) {
  return new HttpResponse({
    body: { data: null, errors: [{ code, messageKey }], success: false },
    status,
  });
}

function parseSearchParams(req: HttpRequest<unknown>): URLSearchParams {
  const raw = req.url.startsWith('http://') || req.url.startsWith('https://')
    ? req.url
    : `http://local${req.url.startsWith('/') ? '' : '/'}${req.url}`;
  return new URL(raw).searchParams;
}

function cityOfId(id: string): MockCity | undefined {
  return mockApiCities.find((c) => c.id === id);
}

function districtOfId(districtId: string): MockDistrict | null {
  for (const list of Object.values(mockApiDistrictsByCity)) {
    const d = list.find((x) => x.id === districtId);

    if (d != null)
      return d;
  }

  return null;
}

/** Lista derivada dos registros ativos (para filtros e paginação). */
function buildActiveListItems(): PublicCatalogListItem[] {
  ensureMockApiPropertyStoreSeeded();
  const items: PublicCatalogListItem[] = [];

  for (const p of mockApiProperties.values()) {
    if (!p.isActive)
      continue;

    const city = cityOfId(p.cityId);

    if (city == null)
      continue;

    const district = districtOfId(p.districtId);
    items.push(toPublicCatalogListItem(p, city, district));
  }

  return items;
}

function parsePositiveInt(raw: string | null, fallback: number, min: number, max?: number): number | null {
  if (raw == null || raw === '')
    return fallback;

  const n = Number.parseInt(raw, 10);

  if (!Number.isFinite(n) || n < min)
    return null;

  if (max != null && n > max)
    return null;

  return n;
}

function parseOptionalDecimal(raw: string | null): number | undefined {
  if (raw == null || raw === '')
    return undefined;

  const n = Number(raw);

  if (!Number.isFinite(n))
    return undefined;

  return n;
}

function parseOptionalInt(raw: string | null): number | undefined {
  if (raw == null || raw === '')
    return undefined;

  const n = Number.parseInt(raw, 10);

  if (!Number.isFinite(n))
    return undefined;

  return n;
}

function matchesListFilters(
  item: PublicCatalogListItem,
  q: {
    citySlug?: string;
    districtSlug?: string;
    maxBedrooms?: number;
    maxParkingSpaces?: number;
    maxPrice?: number;
    minBedrooms?: number;
    minParkingSpaces?: number;
    minPrice?: number;
    propertyType?: number;
  },
): boolean {
  const cityOk = !q.citySlug || item.city.slug === q.citySlug;

  if (!cityOk)
    return false;

  if (q.districtSlug && (!item.district || item.district.slug !== q.districtSlug))
    return false;

  if (q.propertyType != null && item.propertyType !== q.propertyType)
    return false;

  const hasPriceFilter = q.minPrice != null || q.maxPrice != null;

  if (hasPriceFilter && item.price == null)
    return false;

  if (q.minPrice != null && item.price != null && item.price < q.minPrice)
    return false;

  if (q.maxPrice != null && item.price != null && item.price > q.maxPrice)
    return false;

  if (q.minBedrooms != null && (item.bedroomCount == null || item.bedroomCount < q.minBedrooms))
    return false;

  if (q.maxBedrooms != null && (item.bedroomCount == null || item.bedroomCount > q.maxBedrooms))
    return false;

  if (q.minParkingSpaces != null && (item.parkingSpaceCount == null || item.parkingSpaceCount < q.minParkingSpaces))
    return false;

  if (q.maxParkingSpaces != null && (item.parkingSpaceCount == null || item.parkingSpaceCount > q.maxParkingSpaces))
    return false;

  return true;
}

/** Combinação cidade+bairro inválida → lista vazia (HTTP 200). */
function isDistrictValidForCity(citySlug: string, districtSlug: string): boolean {
  const city = mockApiCities.find((c) => c.slug === citySlug);

  if (city == null)
    return false;

  const rows = mockApiDistrictsByCity[city.id] ?? [];

  return rows.some((d) => d.slug === districtSlug);
}

export const mockApiPublicCatalogHandlers: AdminMockRequestHandler[] = [
  {
    method: 'GET',
    path: /^\/api\/public\/cities$/,
    handle: (req) => {
      ensureMockApiPropertyStoreSeeded();
      const sp = parseSearchParams(req);
      const pt = parseOptionalInt(sp.get('propertyType'));
      const pool = buildActiveListItems();
      let rows = pool.map((s) => s.city);

      const uniq = new Map<string, (typeof rows)[0]>();

      for (const c of rows)
        uniq.set(c.slug, c);

      rows = [...uniq.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

      if (pt != null)
        rows = rows.filter((c) => pool.some((s) => s.city.slug === c.slug && s.propertyType === pt));

      return of(
        new HttpResponse({
          body: ok(rows),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'GET',
    path: /^\/api\/public\/filters\/property-types$/,
    handle: (req) => {
      ensureMockApiPropertyStoreSeeded();
      const sp = parseSearchParams(req);
      const districtSlug = sp.get('districtSlug') ?? undefined;
      const citySlug = sp.get('citySlug') ?? undefined;

      if (districtSlug && !citySlug)
        return of(fail(400, 'Validation.Failed', 'app.validation'));

      let pool = buildActiveListItems();

      if (citySlug)
        pool = pool.filter((s) => s.city.slug === citySlug);

      if (districtSlug) {
        if (!citySlug || !isDistrictValidForCity(citySlug, districtSlug))
          pool = [];
        else
          pool = pool.filter((s) => s.district?.slug === districtSlug);
      }

      const values = [...new Set(pool.map((s) => s.propertyType).filter((x): x is number => x != null))].sort(
        (a, b) => a - b,
      );

      return of(
        new HttpResponse({
          body: ok({ values }),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'GET',
    path: /^\/api\/public\/filters\/districts$/,
    handle: (req) => {
      ensureMockApiPropertyStoreSeeded();
      const sp = parseSearchParams(req);
      const citySlug = sp.get('citySlug');

      if (citySlug == null || citySlug === '')
        return of(fail(400, 'Validation.Failed', 'app.validation'));

      const pt = parseOptionalInt(sp.get('propertyType'));
      const city = mockApiCities.find((c) => c.slug === citySlug);

      if (city == null) {
        return of(
          new HttpResponse({
            body: ok({ items: [] }),
            status: 200,
          }),
        );
      }

      let pool = buildActiveListItems().filter((s) => s.city.slug === citySlug);

      if (pt != null)
        pool = pool.filter((s) => s.propertyType === pt);

      const bySlugDist = new Map<string, { name: string; slug: string }>();

      for (const s of pool) {
        if (s.district)
          bySlugDist.set(s.district.slug, s.district);
      }

      const items = [...bySlugDist.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

      return of(
        new HttpResponse({
          body: ok({ items }),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'GET',
    path: /^\/api\/public\/properties$/,
    handle: (req) => {
      ensureMockApiPropertyStoreSeeded();
      const sp = parseSearchParams(req);
      const districtSlug = sp.get('districtSlug') ?? undefined;
      const citySlug = sp.get('citySlug') ?? undefined;

      if (districtSlug && !citySlug)
        return of(fail(400, 'Validation.Failed', 'app.validation'));

      const page = parsePositiveInt(sp.get('page'), 1, 1);

      if (page == null)
        return of(fail(400, 'Validation.Failed', 'app.validation'));

      const pageSize = parsePositiveInt(sp.get('pageSize'), 20, 1, 50);

      if (pageSize == null)
        return of(fail(400, 'Validation.Failed', 'app.validation'));

      const minPrice = parseOptionalDecimal(sp.get('minPrice'));
      const maxPrice = parseOptionalDecimal(sp.get('maxPrice'));

      if (minPrice != null && maxPrice != null && minPrice > maxPrice)
        return of(fail(400, 'Validation.Failed', 'app.validation'));

      const minBedrooms = parseOptionalInt(sp.get('minBedrooms'));
      const maxBedrooms = parseOptionalInt(sp.get('maxBedrooms'));

      if (minBedrooms != null && maxBedrooms != null && minBedrooms > maxBedrooms)
        return of(fail(400, 'Validation.Failed', 'app.validation'));

      const minParkingSpaces = parseOptionalInt(sp.get('minParkingSpaces'));
      const maxParkingSpaces = parseOptionalInt(sp.get('maxParkingSpaces'));

      if (minParkingSpaces != null && maxParkingSpaces != null && minParkingSpaces > maxParkingSpaces)
        return of(fail(400, 'Validation.Failed', 'app.validation'));

      const propertyType = parseOptionalInt(sp.get('propertyType'));

      let pool = buildActiveListItems();

      if (districtSlug && citySlug && !isDistrictValidForCity(citySlug, districtSlug))
        pool = [];

      const filtered = pool
        .filter((item) =>
          matchesListFilters(item, {
            citySlug,
            districtSlug,
            maxBedrooms,
            maxParkingSpaces,
            maxPrice,
            minBedrooms,
            minParkingSpaces,
            minPrice,
            propertyType,
          }),
        )
        .sort((a, b) => b.updatedAtUtc.localeCompare(a.updatedAtUtc));

      const totalCount = filtered.length;
      const start = (page - 1) * pageSize;
      const items = filtered.slice(start, start + pageSize);

      return of(
        new HttpResponse({
          body: ok({
            items,
            page,
            pageSize,
            totalCount,
          }),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'GET',
    path: /^\/api\/public\/properties\/([^/]+)$/,
    handle: (_req, m) => {
      ensureMockApiPropertyStoreSeeded();
      const slug = decodeURIComponent(m![1]!);

      const match = [...mockApiProperties.values()].find((p) => p.publicSlug === slug);

      if (match == null || !match.isActive) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false },
            status: 404,
          }),
        );
      }

      const city = cityOfId(match.cityId);

      if (city == null) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false },
            status: 404,
          }),
        );
      }

      const district = districtOfId(match.districtId);
      const detail = toPublicCatalogDetail(match, city, district, match.updatedAtUtc);

      return of(
        new HttpResponse({
          body: ok(detail),
          status: 200,
        }),
      );
    },
  },
];
